import React from 'react';
import { Drawer } from 'antd';
import { FlareLogoMark, useCompactWorkspaceLayout } from '../../index.js';
import { SIDEBAR_WIDTH, createStandaloneSurfaceStyle } from './style.js';
import useSidebarResize from './useSidebarResize.js';
import { StandaloneInlineSidebar, StandaloneSidebarToggleButton } from './components/StandaloneSidebar.jsx';
import SessionPane from './components/SessionPane.jsx';
import MainPane from './components/MainPane.jsx';
import StandaloneMobileHeader from './components/StandaloneMobileHeader.jsx';

function resolveMobileSidebarLogo(resolvedUILabels = {}, themeTokens) {
  const logoUrl = String(resolvedUILabels.sidebar_logo_url || '').trim();
  if (!logoUrl) {
    return (
      <FlareLogoMark
        label={resolvedUILabels.sidebar_logo_label}
        tag={resolvedUILabels.sidebar_logo_tag}
        themeTokens={themeTokens}
      />
    );
  }
  return (
    <img
      src={logoUrl}
      alt={String(resolvedUILabels.sidebar_logo_alt || 'logo')}
      style={{
        display: 'block',
        height: 28,
        maxWidth: '100%',
        objectFit: 'contain',
        width: 'auto',
      }}
    />
  );
}

function resolveStandaloneLayoutPresentation({
  fillContainer,
  isCompactLayout,
  sidebarCollapsed,
  sidebarWidth,
  themeTokens,
}) {
  const showInlineSidebar = !isCompactLayout;
  const sidebarColumnWidth = sidebarCollapsed ? SIDEBAR_WIDTH.collapsed : sidebarWidth;
  return {
    className: `flare-chat-workspace flare-chat-standalone-surface${isCompactLayout ? ' is-compact-layout' : ''}`,
    showInlineSidebar,
    surfaceStyle: {
      ...createStandaloneSurfaceStyle(themeTokens),
      gridTemplateColumns: showInlineSidebar ? `${sidebarColumnWidth}px minmax(0, 1fr)` : 'minmax(0, 1fr)',
      ...(fillContainer ? { height: '100%' } : {}),
    },
  };
}

export default function StandaloneLayout({
  themeTokens,
  viewModel,
  actionGuards,
  apiReadinessGate,
  hasProject,
  projectItems,
  projectSlot,
  activeSession,
  sessions,
  sidebarStamp,
  skillManagement,
  modeManagement,
  projectNameEditing,
  projectNameDraft,
  setProjectNameDraft,
  handleProjectNameSave,
  handleProjectNameCancel,
  projectDisplayName,
  handleProjectNameStartEdit,
  activeWorkspaceTab,
  setActiveWorkspaceTab,
  knowledgeHubPopoverContent,
  knowledgeHubPopoverOpen,
  setKnowledgeHubPopoverOpen,
  handleOpenKnowledgeHub,
  canvasAvailable = false,
  showCanvasPanel,
  canvasFullscreenOpen,
  handleToggleWorkspacePanel,
  handleCloseWorkspacePanel,
  composerNode,
  handleOpenSourcePicker,
  handleRemoveSource,
  handleRetrySource,
  handleViewSourceDetail,
  sourceActionError,
  sourceItems,
  sourceRemovingId,
  sourceSyncLoading,
  sourceUploadLoading,
  canvasPanelNode,
  renderedTimelineItems,
  handleUICardAction,
  handleWorkflowOperationAction,
  generativeRegistry,
  canExpandScenarios,
  showAllScenarios,
  setShowAllScenarios,
  visibleScenarios,
  recommendationPanelDismissed,
  recommendationPanelTitle,
  handleScenarioCardClick,
  sourceFileInputRef,
  handleSourceFileChange,
  fillContainer = false,
  contentMaxWidth,
  resolvedProductName,
  resolvedProductTag,
  resolvedUILabels,
}) {
  const surfaceRef = React.useRef(null);
  const isCompactLayout = useCompactWorkspaceLayout(surfaceRef);
  const [sidebarDrawerOpen, setSidebarDrawerOpen] = React.useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const resize = useSidebarResize(surfaceRef, !isCompactLayout && !sidebarCollapsed, showCanvasPanel, canvasFullscreenOpen);

  React.useEffect(() => {
    if (!isCompactLayout) {
      setSidebarDrawerOpen(false);
    }
  }, [isCompactLayout]);

  const { className, showInlineSidebar, surfaceStyle } = resolveStandaloneLayoutPresentation({
    fillContainer,
    isCompactLayout,
    sidebarCollapsed,
    sidebarWidth: resize.width,
    themeTokens,
  });
  const handleSidebarToggle = React.useCallback(() => setSidebarCollapsed((current) => !current), []);
  const handleOpenSidebarMenu = React.useCallback(() => setSidebarDrawerOpen(true), []);
  const handleCloseSidebarMenu = React.useCallback(() => setSidebarDrawerOpen(false), []);
  const sidebarToggleButton = (
    <StandaloneSidebarToggleButton
      onToggle={handleSidebarToggle}
      show={showInlineSidebar}
      sidebarCollapsed={sidebarCollapsed}
      themeTokens={themeTokens}
    />
  );
  const mobileSidebarTitle = (
    <div className="flare-chat-mobile-session-drawer-brand">
      {resolveMobileSidebarLogo(resolvedUILabels, themeTokens)}
    </div>
  );
  const mobileHeaderNode = (
    <StandaloneMobileHeader
      show={isCompactLayout}
      brandNode={mobileSidebarTitle}
      menuOpen={sidebarDrawerOpen}
      onOpenMenu={handleOpenSidebarMenu}
      themeTokens={themeTokens}
    />
  );

  const standaloneSurface = (
    <div
      className={className}
      ref={surfaceRef}
      style={surfaceStyle}
    >
      <StandaloneInlineSidebar
        show={showInlineSidebar}
        themeTokens={themeTokens}
      >
        <SessionPane
          managementAvailable={Boolean(skillManagement || modeManagement)}
          themeTokens={themeTokens}
          viewModel={viewModel}
          actionGuards={actionGuards}
          apiReadinessGate={apiReadinessGate}
          hasProject={hasProject}
          projectItems={projectItems}
          projectSlot={projectSlot}
          sessions={sessions}
          resolvedUILabels={resolvedUILabels}
          setActiveWorkspaceTab={setActiveWorkspaceTab}
          sidebarHeaderActions={sidebarToggleButton}
          sidebarStamp={sidebarStamp}
          productName={resolvedProductName}
          collapsed={sidebarCollapsed}
        />
      </StandaloneInlineSidebar>

      {showInlineSidebar && !sidebarCollapsed ? (
        <div aria-label="调整左侧栏宽度" aria-orientation="vertical" role="separator"
          aria-valuemin={SIDEBAR_WIDTH.min} aria-valuemax={resize.maximum} aria-valuenow={resize.width}
          onPointerDown={resize.start} onPointerMove={resize.move} onPointerUp={resize.finish}
          onPointerCancel={resize.finish} onLostPointerCapture={resize.finish}
          style={{ position: 'absolute', left: resize.width - 5, top: 0, bottom: 0, width: 10,
            zIndex: 20, cursor: 'col-resize', touchAction: 'none', userSelect: 'none' }}>
          <span aria-hidden="true" style={{ position: 'absolute', top: '50%', left: 4, height: 30,
            width: 3, borderInline: `1px solid ${themeTokens.divider}` }} />
        </div>
      ) : null}

      <div
        className="flare-standalone-content-shell"
        style={{ display: 'grid', gridTemplateRows: 'minmax(0, 1fr)', minHeight: 0, minWidth: 0 }}
      >
      <MainPane
        mobileHeaderNode={mobileHeaderNode}
        themeTokens={themeTokens}
        hasProject={hasProject}
        viewModel={viewModel}
        projectSlot={projectSlot}
        projectNameEditing={projectNameEditing}
        projectNameDraft={projectNameDraft}
        setProjectNameDraft={setProjectNameDraft}
        handleProjectNameSave={handleProjectNameSave}
        handleProjectNameCancel={handleProjectNameCancel}
        projectDisplayName={projectDisplayName}
        handleProjectNameStartEdit={handleProjectNameStartEdit}
        activeWorkspaceTab={activeWorkspaceTab}
        setActiveWorkspaceTab={setActiveWorkspaceTab}
        skillManagement={skillManagement} modeManagement={modeManagement}
        resolvedProductName={resolvedProductName}
        resolvedProductTag={resolvedProductTag}
        resolvedUILabels={resolvedUILabels}
        activeSession={activeSession}
        knowledgeHubPopoverContent={knowledgeHubPopoverContent}
        knowledgeHubPopoverOpen={knowledgeHubPopoverOpen}
        setKnowledgeHubPopoverOpen={setKnowledgeHubPopoverOpen}
        handleOpenKnowledgeHub={handleOpenKnowledgeHub}
        canvasAvailable={canvasAvailable}
        showCanvasPanel={showCanvasPanel}
        canvasFullscreenOpen={canvasFullscreenOpen}
        handleToggleWorkspacePanel={handleToggleWorkspacePanel}
        handleCloseWorkspacePanel={handleCloseWorkspacePanel}
        projectItems={projectItems}
        actionGuards={actionGuards}
        composerNode={composerNode}
        contentMaxWidth={contentMaxWidth}
        handleOpenSourcePicker={handleOpenSourcePicker}
        handleRemoveSource={handleRemoveSource}
        handleRetrySource={handleRetrySource}
        handleViewSourceDetail={handleViewSourceDetail}
        sourceActionError={sourceActionError}
        sourceItems={sourceItems}
        sourceRemovingId={sourceRemovingId}
        sourceSyncLoading={sourceSyncLoading}
        sourceUploadLoading={sourceUploadLoading}
        canvasPanelNode={canvasPanelNode}
        renderedTimelineItems={renderedTimelineItems}
        handleUICardAction={handleUICardAction}
        handleWorkflowOperationAction={handleWorkflowOperationAction}
        generativeRegistry={generativeRegistry}
        canExpandScenarios={canExpandScenarios}
        showAllScenarios={showAllScenarios}
        setShowAllScenarios={setShowAllScenarios}
        visibleScenarios={visibleScenarios}
        recommendationPanelDismissed={recommendationPanelDismissed}
        recommendationPanelTitle={recommendationPanelTitle}
        handleScenarioCardClick={handleScenarioCardClick}
        isCompactLayout={isCompactLayout}
        showSidebarMenuButton={isCompactLayout}
        handleOpenSidebarMenu={handleOpenSidebarMenu}
      />
      </div>

      <Drawer
        getContainer={false}
        onClose={handleCloseSidebarMenu}
        open={isCompactLayout && sidebarDrawerOpen}
        placement="left"
        rootClassName="flare-chat-mobile-session-drawer-root"
        rootStyle={{ position: 'absolute' }}
        styles={{
          body: { padding: 0 },
          wrapper: { width: '86%' },
        }}
        title={mobileSidebarTitle}
      >
        <SessionPane
          managementAvailable={Boolean(skillManagement || modeManagement)}
          themeTokens={themeTokens}
          viewModel={viewModel}
          actionGuards={actionGuards}
          apiReadinessGate={apiReadinessGate}
          hasProject={hasProject}
          projectItems={projectItems}
          projectSlot={projectSlot}
          sessions={sessions}
          resolvedUILabels={resolvedUILabels}
          setActiveWorkspaceTab={setActiveWorkspaceTab}
          mobileSidebar
          showHeader={false}
          onAfterSidebarAction={handleCloseSidebarMenu}
        />
      </Drawer>

      <input
        ref={sourceFileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleSourceFileChange}
      />
    </div>
  );

  return standaloneSurface;
}
